import React, { useState } from 'react';
import NeonDivider from './NeonDivider';
import { PintIcon, CanIcon, BottleIcon } from './Icons';
import menuData from '../data/menu.json';

const MenuSection = () => {
  const [activeTab, setActiveTab] = useState('wings');
  const [activeSubTab, setActiveSubTab] = useState('');
  const [expandedAccordions, setExpandedAccordions] = useState({});

  const tabs = [
    { id: 'wings', label: 'Wings', mobileLabel: 'Wings', icon: '🔥' },
    { id: 'food', label: 'Food', mobileLabel: 'Food', icon: '🍽️' },
    { id: 'beer', label: 'Beer', mobileLabel: 'Beer', icon: '🍺' },
    { id: 'drinks', label: 'Drinks', mobileLabel: 'Drinks', icon: '🍹' },
    { id: 'extras', label: 'Extras', mobileLabel: 'Extras', icon: '🍰' }
  ];

  const subTabs = {
    food: [
      { id: 'apps', label: 'Apps & Starters', mobileLabel: 'Apps' },
      { id: 'fries', label: 'Billy\'s Fries', mobileLabel: 'Fries' },
      { id: 'pubGrub', label: 'Pub Grub', mobileLabel: 'Mains' },
      { id: 'salads', label: 'Big Salads', mobileLabel: 'Salads' }
    ],
    beer: [
      { id: 'beerDraught', label: 'Draught', mobileLabel: 'Draft', icon: PintIcon },
      { id: 'beerCans', label: 'Cans', mobileLabel: 'Cans', icon: CanIcon },
      { id: 'beerBottles', label: 'Bottles', mobileLabel: 'Bottles', icon: BottleIcon }
    ],
    drinks: [
      { id: 'cocktails', label: 'Cocktails', mobileLabel: 'Cocktails' },
      { id: 'spirits', label: 'Spirits', mobileLabel: 'Spirits' },
      { id: 'wine', label: 'Wine', mobileLabel: 'Wine' },
      { id: 'coolers', label: 'Coolers', mobileLabel: 'Coolers' }
    ],
    extras: [
      { id: 'desserts', label: 'Desserts', mobileLabel: 'Desserts' },
      { id: 'beverages', label: 'Hot Drinks & Soft Drinks', mobileLabel: 'Beverages' }
    ]
  };

  // Set default sub-tab when main tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setExpandedAccordions({}); // Reset all accordions when switching tabs
    if (subTabs[tabId]) {
      setActiveSubTab(subTabs[tabId][0].id);
    } else {
      setActiveSubTab('');
    }
  };

  // Initialize sub-tab for food on first load
  React.useEffect(() => {
    if (activeTab === 'food' && !activeSubTab) {
      setActiveSubTab('apps');
    }
  }, [activeTab, activeSubTab]);

  const getCurrentMenuData = () => {
    if (activeTab === 'wings') {
      return menuData.wings;
    } else if (activeSubTab) {
      return menuData[activeSubTab];
    }
    return null;
  };

  const currentMenu = getCurrentMenuData();

  const toggleAccordion = (itemIndex, sectionType) => {
    const key = `${itemIndex}-${sectionType}`;
    setExpandedAccordions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isExpanded = (itemIndex, sectionType) => {
    const key = `${itemIndex}-${sectionType}`;
    return expandedAccordions[key] || false;
  };

  const renderAccordionSection = (title, items, colorClass, iconClass, itemIndex, sectionType) => {
    if (!items || items.length === 0) return null;
    
    const expanded = isExpanded(itemIndex, sectionType);
    
    return (
      <div className="mt-3">
        <button
          onClick={() => toggleAccordion(itemIndex, sectionType)}
          className={`w-full flex items-center justify-between p-2 sm:p-3 rounded-lg border border-gray/20 
                     hover:border-gray/40 transition-all duration-200 bg-black/30 hover:bg-black/50`}
        >
          <div className="flex items-center gap-1 sm:gap-2">
            <span className={`text-sm sm:text-lg ${iconClass}`}>
              {sectionType === 'modifications' && '⚙️'}
              {sectionType === 'additions' && '➕'}
              {sectionType === 'substitutions' && '🔄'}
            </span>
            <span className={`text-xs sm:text-sm font-semibold ${colorClass}`}>
              {title} ({items.length})
            </span>
          </div>
          <span className={`transform transition-transform duration-200 text-gray text-xs sm:text-sm ${expanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>
        
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
          expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="pt-2 pb-1 px-1 sm:px-2">
            <div className="space-y-1 sm:space-y-2">
              {items.map((item, index) => (
                <div key={index} className="flex justify-between items-start text-xs sm:text-sm p-2 rounded-md bg-gray/5 hover:bg-gray/10 transition-colors">
                  <div className="flex-1 min-w-0">
                    <span className="text-gray font-medium block">{item.name}</span>
                    {item.description && (
                      <p className="text-xs text-gray/70 mt-1 leading-relaxed break-words">{item.description}</p>
                    )}
                  </div>
                  <span className="text-neonRed font-bold ml-2 sm:ml-3 flex-shrink-0 text-xs sm:text-sm">
                    {item.price}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section 
      id="menu" 
      className="py-16 sm:py-20 px-2 sm:px-4 relative
                 /* Chalkboard texture for desktop, flat for mobile */
                 bg-black
                 sm:bg-gradient-to-br sm:from-gray-900 sm:via-black sm:to-gray-800
                 sm:before:content-[''] sm:before:absolute sm:before:inset-0 
                 sm:before:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.02)_1px,transparent_1px)]
                 sm:before:bg-[length:40px_40px] sm:before:opacity-30
                 sm:after:content-[''] sm:after:absolute sm:after:inset-0
                 sm:after:bg-[linear-gradient(45deg,transparent_49%,rgba(255,255,255,0.01)_50%,transparent_51%)]
                 sm:after:bg-[length:8px_8px] sm:after:opacity-20"
    >
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-logoSans text-ivory mb-4">
            THE MENU
          </h2>
          <p className="text-gray text-base sm:text-lg max-w-2xl mx-auto px-4">
            From legendary wings to craft cocktails, every bite and sip 
            delivers the bold flavors Billy's is famous for.
          </p>
        </div>

        <NeonDivider color="neonBlue" />

        {/* Main Tab Navigation */}
        <div className="flex justify-center mb-6 sm:mb-8 px-2">
          <div className="flex gap-1 sm:gap-2 bg-black/30 rounded-lg p-1 sm:p-2 w-full max-w-lg sm:max-w-2xl">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 px-2 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all duration-200 min-h-[48px] sm:min-h-[52px] text-xs sm:text-base ${
                  activeTab === tab.id
                    ? 'bg-neonBlue text-black shadow-neonBlue'
                    : 'text-ivory hover:text-neonBlue hover:bg-black/50'
                }`}
              >
                <span className="text-sm sm:text-base">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden text-xs font-medium">{tab.mobileLabel}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Sub Tab Navigation */}
        {subTabs[activeTab] && (
          <div className="flex justify-center mb-6 sm:mb-8 px-2">
            <div className="flex gap-1 sm:gap-2 bg-black/20 rounded-lg p-1 sm:p-2 w-full max-w-full overflow-x-auto">
              <div className="flex gap-1 sm:gap-2 min-w-max mx-auto">
                {subTabs[activeTab].map((subTab) => {
                  const IconComponent = subTab.icon;
                  return (
                    <button
                      key={subTab.id}
                      onClick={() => setActiveSubTab(subTab.id)}
                      className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm whitespace-nowrap ${
                        activeSubTab === subTab.id
                          ? 'bg-neonRed text-black shadow-neonRed'
                          : 'text-ivory hover:text-neonRed hover:bg-black/50'
                      }`}
                    >
                      {IconComponent && <IconComponent className="h-3 w-3 sm:h-4 sm:w-4" />}
                      <span className="hidden sm:inline">{subTab.label}</span>
                      <span className="sm:hidden">{subTab.mobileLabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Menu Title */}
        {currentMenu && (
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-logoSans text-white mb-2">
              {currentMenu.title}
            </h3>
            <div className="w-16 sm:w-24 h-0.5 bg-gradient-to-r from-neonBlue to-neonRed mx-auto"></div>
          </div>
        )}

        {/* Menu Items Grid */}
        {currentMenu && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 xl:gap-8 mb-12 sm:mb-16 px-2">
            {currentMenu.items.map((item, index) => (
              <div
                key={index}
                className="p-3 sm:p-4 lg:p-6 border border-gray/10 rounded-xl hover:shadow-neonBlue 
                           hover:ring-2 hover:ring-neonBlue hover:ring-offset-2 
                           hover:ring-offset-black transition-all duration-200 
                           bg-black/20 backdrop-blur-sm group
                           /* Mobile optimizations */
                           touch-target mobile-padding card-mobile"
              >
                {/* Main Item */}
                <div className="flex flex-col gap-2 sm:gap-1 mb-3">
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-logoSans text-sm sm:text-base lg:text-lg text-white font-medium leading-tight flex items-center group-hover:text-neonBlue transition-colors flex-1">
                      {item.name}
                      {item.lateNight && (
                        <span className="ml-2 text-xs">🌙</span>
                      )}
                    </h4>
                    <span className="text-neonRed font-bold text-base sm:text-lg lg:text-xl flex-shrink-0">
                      {item.price}
                    </span>
                  </div>
                </div>
                <p className="text-gray text-xs sm:text-sm lg:text-base leading-relaxed mb-3">
                  {item.description}
                </p>

                {/* Accordion Sections */}
                {renderAccordionSection("Modifications", item.modifications, "text-neonBlue", "text-neonBlue", index, "modifications")}
                {renderAccordionSection("Add-Ons", item.additions, "text-green-400", "text-green-400", index, "additions")}
                {renderAccordionSection("Substitutions", item.substitutions, "text-yellow-400", "text-yellow-400", index, "substitutions")}
              </div>
            ))}
          </div>
        )}

        {/* Special Features for Beer/Drinks */}
        {(activeTab === 'beer' || activeTab === 'drinks') && (
          <>
            <NeonDivider color="neonRed" />
            <div className="text-center mt-6 sm:mt-8 px-4">
              <p className="text-gray text-sm sm:text-base lg:text-lg max-w-xl mx-auto">
                {activeTab === 'beer' 
                  ? "From local craft to international favorites, we've got the perfect brew for every taste."
                  : "Handcrafted cocktails and premium spirits - the perfect complement to our legendary wings."
                }
              </p>
            </div>
          </>
        )}

        {/* Wings Special Note */}
        {activeTab === 'wings' && currentMenu && (
          <>
            <NeonDivider color="neonRed" />
            <div className="mt-6 sm:mt-8 px-4">
              {/* Wing Notes */}
              {currentMenu.notes && (
                <div className="text-center mb-6">
                  {currentMenu.notes.map((note, index) => (
                    <p key={index} className="text-gray text-sm sm:text-base lg:text-lg max-w-2xl mx-auto mb-2">
                      {note}
                    </p>
                  ))}
                </div>
              )}

              {/* Wing Styles, Sauces, and Rubs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
                
                {/* Wing Styles */}
                {currentMenu.wingStyles && (
                  <div className="p-4 border border-gray/20 rounded-xl bg-black/20 backdrop-blur-sm">
                    <h4 className="text-neonBlue font-logoSans text-lg mb-3 text-center">Wing Styles</h4>
                    <ul className="space-y-2 text-gray text-sm">
                      {currentMenu.wingStyles.map((style, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <span className="text-neonRed">•</span>
                          <span>{style}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Base Sauces */}
                {currentMenu.baseSauces && (
                  <div className="p-4 border border-gray/20 rounded-xl bg-black/20 backdrop-blur-sm">
                    <h4 className="text-yellow-400 font-logoSans text-lg mb-3 text-center">Base Sauces</h4>
                    <div className="flex flex-wrap gap-1 text-gray text-xs">
                      {currentMenu.baseSauces.map((sauce, index) => (
                        <span key={index} className="bg-gray/10 px-2 py-1 rounded">
                          {sauce}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Heat Options */}
                {currentMenu.heatOptions && (
                  <div className="p-4 border border-gray/20 rounded-xl bg-black/20 backdrop-blur-sm">
                    <h4 className="text-neonRed font-logoSans text-lg mb-3 text-center">Heat Options</h4>
                    <ul className="space-y-2 text-gray text-sm">
                      {currentMenu.heatOptions.map((heat, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <span className="text-neonRed">🔥</span>
                          <span>{heat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Dry Rubs */}
                {currentMenu.dryRubs && (
                  <div className="p-4 border border-gray/20 rounded-xl bg-black/20 backdrop-blur-sm md:col-span-2 lg:col-span-1">
                    <h4 className="text-green-400 font-logoSans text-lg mb-3 text-center">Dry Rubs</h4>
                    <div className="flex flex-wrap gap-1 text-gray text-xs">
                      {currentMenu.dryRubs.map((rub, index) => (
                        <span key={index} className="bg-gray/10 px-2 py-1 rounded">
                          {rub}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* General Notes for other sections */}
        {currentMenu && currentMenu.notes && activeTab !== 'wings' && (
          <div className="text-center mt-6 sm:mt-8 px-4">
            {currentMenu.notes.map((note, index) => (
              <p key={index} className="text-gray text-sm sm:text-base max-w-xl mx-auto mb-2">
                {note}
              </p>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default MenuSection; 